/** 资产数据列表可配置列（与导出字段对齐，操作列固定展示）
 *
 * 订单附件（orderAttachment）默认隐藏：金蝶订单模块极少原生挂附件，
 * 2806 行里仅 5 行完整 / 2801 行缺失，信息密度过低。
 * 保留在 LIST_COLUMN_DEFS 里供"设置列"手动开启与导出字段使用。
 */
export const LIST_COLUMN_DEFS: { key: string; label: string; sortable?: boolean; defaultHidden?: boolean }[] = [
  { key: 'contractAttachment', label: '合同附件' },
  { key: 'orderAttachment', label: '订单附件', defaultHidden: true },
  { key: 'receiptAttachment', label: '收款附件' },
  { key: 'logisticsAttachment', label: '物流附件' },
  { key: 'invoiceAttachment', label: '发票附件' },
  { key: 'id', label: '数据项ID', sortable: true },
  { key: 'name', label: '数据名称', sortable: true },
  { key: 'source', label: '来源', sortable: true },
  { key: 'type', label: '类型', sortable: true },
  { key: 'documentStatus', label: '单据状态', sortable: true },
  { key: 'onChain', label: '上链状态', sortable: true },
  { key: 'contractNo', label: '合同号', sortable: true },
  { key: 'contractType', label: '合同类型', sortable: true },
  { key: 'customer', label: '客户', sortable: true },
  { key: 'settlementOrg', label: '结算组织', sortable: true },
  { key: 'productName', label: '商品名称', sortable: true },
  { key: 'rwaCategory', label: '业务分类', sortable: true },
  { key: 'priceTaxTotal', label: '应收净额', sortable: true },
  { key: 'receiptAmount', label: '收款金额', sortable: true },
  { key: 'partialReceipt', label: '部分收款', sortable: true },
  { key: 'unreceivedAmount', label: '未收款金额' },
  { key: 'invoicedAmount', label: '已开票额', sortable: true },
  { key: 'contractCreatedDate', label: '合同创建日期', sortable: true },
  { key: 'contractReceiptDate', label: '合同收款日期', sortable: true },
  { key: 'status', label: '状态', sortable: true },
  { key: 'createdTime', label: '创建时间', sortable: true },
]

export const DEFAULT_VISIBLE_COLUMN_KEYS = LIST_COLUMN_DEFS.filter((c) => !c.defaultHidden).map((c) => c.key)

export const VISIBLE_COLUMNS_STORAGE_KEY = 'data_visualization_visible_columns_v1'
export const COLUMN_ORDER_STORAGE_KEY = 'data_visualization_column_order_v1'

const ALL_COLUMN_KEY_SET = new Set(DEFAULT_VISIBLE_COLUMN_KEYS)

/** 合并存储顺序与默认列，补全新增字段 */
export function normalizeColumnOrder(order: string[] | null | undefined): string[] {
  const seen = new Set<string>()
  const result: string[] = []
  for (const k of order ?? []) {
    if (ALL_COLUMN_KEY_SET.has(k) && !seen.has(k)) {
      result.push(k)
      seen.add(k)
    }
  }
  for (const k of DEFAULT_VISIBLE_COLUMN_KEYS) {
    if (!seen.has(k)) result.push(k)
  }
  return result
}

export function loadColumnOrder(): string[] {
  try {
    const raw = localStorage.getItem(COLUMN_ORDER_STORAGE_KEY)
    if (!raw) return DEFAULT_VISIBLE_COLUMN_KEYS
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return DEFAULT_VISIBLE_COLUMN_KEYS
    return normalizeColumnOrder(parsed.filter((k): k is string => typeof k === 'string'))
  } catch {
    return DEFAULT_VISIBLE_COLUMN_KEYS
  }
}

export function saveColumnOrder(keys: string[]): void {
  try {
    localStorage.setItem(COLUMN_ORDER_STORAGE_KEY, JSON.stringify(normalizeColumnOrder(keys)))
  } catch {
    /* ignore quota */
  }
}

export function reorderColumnKeys(order: string[], fromKey: string, toKey: string): string[] {
  if (fromKey === toKey) return order
  const keys = normalizeColumnOrder(order)
  const fromIdx = keys.indexOf(fromKey)
  const toIdx = keys.indexOf(toKey)
  if (fromIdx < 0 || toIdx < 0) return keys
  const next = [...keys]
  const [moved] = next.splice(fromIdx, 1)
  next.splice(toIdx, 0, moved)
  return next
}

export function loadVisibleColumnKeys(): string[] {
  try {
    const raw = localStorage.getItem(VISIBLE_COLUMNS_STORAGE_KEY)
    const order = loadColumnOrder()
    if (!raw) return order
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed) || !parsed.length) return order
    const saved = parsed.filter((k): k is string => typeof k === 'string' && ALL_COLUMN_KEY_SET.has(k))
    if (!saved.length) return order
    const savedSet = new Set(saved)
    const orderedVisible = order.filter((k) => savedSet.has(k))
    for (const k of saved) {
      if (!orderedVisible.includes(k)) orderedVisible.push(k)
    }
    return orderedVisible
  } catch {
    return DEFAULT_VISIBLE_COLUMN_KEYS
  }
}

export function saveVisibleColumnKeys(keys: string[]): void {
  try {
    localStorage.setItem(VISIBLE_COLUMNS_STORAGE_KEY, JSON.stringify(keys))
  } catch {
    /* ignore quota */
  }
}
